/**
 * Corporate Structure View — orchestrator with tabs for timeline,
 * activity, task tree, agent stream, structure map, and entities.
 *
 * Uses DB-backed corporateEntries for live data (TaskLoop architecture).
 * Backward-compatible with legacy split-action runs.
 */

import { useState, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { Button } from "@/ui/primitives/button";
import { Badge } from "@/ui/primitives/badge";
import { DocumentDetail } from "../../documents/DocumentDetail";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/ui/composed/sections/tabs";
import { AiDisclaimer } from "@/ui/composed/ai-disclaimer";
import {
  ArrowLeft,
  RotateCcw,
  Building2,
  Loader2,
  XCircle,
  Bot,
  List,
  Network,
  MessageSquare,
  UserRoundSearch,
  Plus,
} from "lucide-react";
import { formatDuration } from "../shared";
import { StatusDot } from "../StatusBadges";
import { UnifiedFeed, getTaskTree } from "../shared-panels";
import { SummaryChatSheet } from "../SummaryChatSheet";
import { ExportButton } from "@/ui/composed/ExportButton";
import { StructureMap } from "./structure-map";
import { EntitiesDataTable } from "./entities";
import { useCorporateEntityData } from "./entities/useCorporateEntityData";
import type { SubAgentInfo, Group, PrimaryEntity } from "./types";
import { CorporateSummaryPanel } from "./summary";
import { AddCorporateEntityPanel } from "./AddCorporateEntityPanel";
import { caseTypeFromAppointmentType } from "@/lib/precedent-templates";

interface Props {
  taskId: Id<"summarizationTasks">;
  jobId: Id<"jobs">;
  summarizationRunId: Id<"summarizationRuns">;
  onBack: () => void;
  onRerun: () => void;
  isViewer?: boolean;
}

export function CorporateStructureView({
  taskId,
  jobId,
  onBack,
  onRerun,
  isViewer,
}: Props) {
  const [selectedDocumentId, setSelectedDocumentId] =
    useState<Id<"documents"> | null>(null);
  const [chatOpen, setChatOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);

  const handleDocumentClick = useCallback((documentId: string) => {
    setSelectedDocumentId(documentId as Id<"documents">);
  }, []);

  const { data: task } = useQuery(
    convexQuery(
      api.jobs.summarization.publicQueries.getSummarizationTaskDetail,
      { taskId },
    ),
  );

  // Personal-insolvency jobs drop the "Corporate" qualifier from this view's
  // headings ("Corporate Structure" → "Structure"), since the estate is an
  // individual, not a company.
  const { data: job } = useQuery(
    convexQuery(api.jobs.queries.getJob, { jobId }),
  );
  const isBankruptcy =
    caseTypeFromAppointmentType(job?.appointmentType) === "personal";
  const structureTitle = isBankruptcy ? "Structure" : "Corporate Structure";

  // DB-backed entity data (live updates during extraction)
  const { primaryEntity, groups, output, totalEntities, displayEntityCount } =
    useCorporateEntityData(jobId);

  if (!task) return null;

  const isRunning = task.status === "running";
  const isCompleted = task.status === "completed";
  const isFailed = task.status === "failed";
  const isWaiting = task.status === "waiting";

  // Workingmemory shapes vary by runner (v1 3-phase, TaskLoop, Managed Agents v2).
  // The Agent feed cares about taskTree/subAgents; the Entities + Structure Map
  // tabs always read from the DB-backed `corporateEntries` table.
  const workingMemory = task.workingMemory as
    { subAgents?: SubAgentInfo[]; phase?: string } | undefined;
  const taskTree = getTaskTree(task.workingMemory);
  const subAgents = workingMemory?.subAgents;

  // Legacy fallback only — for ancient runs that completed before the
  // corporateEntries table existed and only persisted to task.output.
  // New runs (v1 pipeline + Managed Agents v2) write to corporateEntries,
  // so prefer DB data whenever it's present.
  const legacyOutput = task.output as
    | {
        primaryEntity?: PrimaryEntity | null;
        groups?: Group[];
        documentsAnalysed?: number;
      }
    | undefined;
  const hasDbData = totalEntities > 0;
  const hasLegacyData =
    !!legacyOutput?.groups && legacyOutput.groups.length > 0;

  const effectiveOutput = hasDbData
    ? output
    : hasLegacyData && legacyOutput
      ? {
          primaryEntity: legacyOutput.primaryEntity ?? null,
          groups: legacyOutput.groups ?? [],
          documentsAnalysed: legacyOutput.documentsAnalysed ?? 0,
        }
      : null;
  const effectivePrimary = hasDbData
    ? primaryEntity
    : (legacyOutput?.primaryEntity ?? null);
  const effectiveGroups = hasDbData ? groups : (legacyOutput?.groups ?? []);
  const hasData = effectiveOutput !== null && effectiveGroups.length > 0;

  return (
    <Tabs
      defaultValue={isRunning ? "feed" : "summary"}
      className="-m-6 flex flex-1 flex-col bg-muted px-6 pb-6 [overflow-anchor:none]"
    >
      {/* Sticky header + tab bar — pins below the dashboard header */}
      <div className="sticky top-16 z-[9] -mx-6 bg-muted px-6 pb-2 pt-6">
        <div className="flex items-center gap-3 pb-4">
          <Button
            variant="outline"
            size="sm"
            onClick={onBack}
            className="gap-1.5"
          >
            <ArrowLeft className="h-4 w-4" /> Back
          </Button>
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <StatusDot status={task.status} />
            <h3 className="text-lg font-semibold">{structureTitle}</h3>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {task.duration != null && (
              <Badge variant="outline" className="text-xs tabular-nums">
                {formatDuration(task.duration)}
              </Badge>
            )}
            {isCompleted && effectiveOutput && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setChatOpen(true)}
                className="gap-1.5"
              >
                <MessageSquare className="h-3.5 w-3.5" /> Chat
              </Button>
            )}
            {isCompleted && effectiveOutput && (
              <ExportButton
                content={{
                  title: structureTitle,
                  output: effectiveOutput as Record<string, unknown>,
                }}
                filename={
                  isBankruptcy
                    ? "Structure_Summary"
                    : "Corporate_Structure_Summary"
                }
                formats={["word", "pdf"]}
              />
            )}
            {!isViewer && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setAddOpen(true)}
                className="gap-1.5"
              >
                <Plus className="h-3.5 w-3.5" /> Add entity
              </Button>
            )}
            {(isRunning || isCompleted || isFailed) && (
              <Button
                variant="outline"
                size="sm"
                onClick={onRerun}
                disabled={isViewer}
                className="gap-1.5"
              >
                <RotateCcw className="h-3.5 w-3.5" /> Re-run
              </Button>
            )}
          </div>
        </div>

        {!isWaiting && (
          <TabsList className="w-full">
            <TabsTrigger value="summary" className="gap-1.5">
              <List className="h-3.5 w-3.5" />
              Summary
            </TabsTrigger>
            <TabsTrigger value="map" className="gap-1.5">
              <Network className="h-3.5 w-3.5" />
              Structure Map
            </TabsTrigger>
            <TabsTrigger value="entities" className="gap-1.5">
              <UserRoundSearch className="h-3.5 w-3.5" />
              Entities
              {displayEntityCount > 0 && (
                <Badge variant="secondary" className="ml-0.5 px-1 text-[10px]">
                  {displayEntityCount}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="feed" className="gap-1.5">
              <Bot className="h-3.5 w-3.5" />
              Agent
              {isRunning && (
                <span className="relative ml-1 flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
                </span>
              )}
            </TabsTrigger>
          </TabsList>
        )}
      </div>

      {/* Waiting state */}
      {isWaiting && (
        <div className="flex flex-col items-center gap-3 py-16">
          <Building2 className="h-10 w-10 text-muted-foreground/40" />
          <p className="text-sm text-muted-foreground">
            Click Run to extract corporate entities.
          </p>
        </div>
      )}

      {/* Tab content */}
      {!isWaiting && (
        <>
          <TabsContent value="summary" className="mt-2">
            <CorporateSummaryPanel
              jobId={jobId}
              isRunning={isRunning}
              onViewDocument={handleDocumentClick}
              isBankruptcy={isBankruptcy}
            />
          </TabsContent>

          <TabsContent value="feed" className="mt-2">
            <div className="rounded-xl bg-background p-4 shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.06)]">
              <UnifiedFeed
                task={task}
                subAgents={subAgents ?? []}
                taskTree={taskTree}
                isRunning={isRunning}
              />
            </div>
          </TabsContent>

          <TabsContent value="map" className="mt-2">
            {hasData ? (
              <StructureMap
                primaryEntity={effectivePrimary}
                groups={effectiveGroups}
                onDocumentClick={handleDocumentClick}
              />
            ) : isRunning ? (
              <div className="flex flex-col items-center gap-3 py-16">
                <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
                <p className="text-sm text-muted-foreground">
                  Building structure map...
                </p>
              </div>
            ) : (
              <p className="py-8 text-center text-sm text-muted-foreground">
                No structure data yet.
              </p>
            )}
          </TabsContent>

          <TabsContent value="entities" className="mt-2">
            {isFailed && !hasData && (
              <div className="flex flex-col items-center gap-3 py-16">
                <XCircle className="h-8 w-8 text-destructive" />
                <p className="text-sm text-destructive">
                  {task.error ?? "Unknown error"}
                </p>
              </div>
            )}
            {isRunning && !hasData && (
              <div className="flex flex-col items-center gap-3 py-16">
                <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
                <p className="text-sm text-muted-foreground">
                  Extracting entities...
                </p>
              </div>
            )}
            {hasData && effectiveOutput && (
              <EntitiesDataTable
                output={effectiveOutput}
                onDocumentClick={handleDocumentClick}
              />
            )}
            {!isRunning && !isCompleted && !isFailed && !hasData && (
              <p className="py-8 text-center text-sm text-muted-foreground">
                No output yet.
              </p>
            )}
          </TabsContent>
        </>
      )}

      {/* Token usage footer */}
      {task.tokensUsed && (
        <div className="mt-4 flex items-center gap-4 border-t pt-3 text-xs text-muted-foreground">
          <span>Total tokens:</span>
          <span className="tabular-nums">
            {task.tokensUsed.input.toLocaleString()} in
          </span>
          <span className="tabular-nums">
            {task.tokensUsed.output.toLocaleString()} out
          </span>
          {displayEntityCount > 0 && (
            <span className="text-muted-foreground/60">
              ({displayEntityCount} entities)
            </span>
          )}
        </div>
      )}

      {/* Document detail sheet */}
      <DocumentDetail
        documentId={selectedDocumentId}
        isViewer={isViewer ?? false}
        open={selectedDocumentId !== null}
        onClose={() => setSelectedDocumentId(null)}
      />

      {/* Chat sheet */}
      <SummaryChatSheet
        open={chatOpen}
        onOpenChange={setChatOpen}
        taskId={taskId}
        jobId={jobId}
        agentName={structureTitle}
        summaryOutput={effectiveOutput}
      />
      <AiDisclaimer className="mt-4 text-center" />

      {/* Manual add entity sheet */}
      <AddCorporateEntityPanel
        jobId={jobId}
        taskId={taskId}
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onAdded={onRerun}
      />
    </Tabs>
  );
}
